<!-- eslint-disable vue/no-v-html -->
<template>
  <div class="max-container py-6 lg:py-8">
    <h2 :id="header?.toLowerCase().replace(/\s+/g, '-').replace(/[^\w-]/g, '')">{{ header }}</h2>
    <div class="overflow-x-auto">
      <SdsTable
        :fields="fields"
        :items="items"
        :row-highlight="true"
        :onSort="sortByColumn"
        :sortBy="displaySortColumn"
        :sortDesc="sortDirection === 'desc'"
        density="comfortable"
        class="
          mt-1
          mb-0
          [&_tr:hover_a]:opacity-100!
          [&_tr:hover_i]:opacity-100!
          [&_tr:hover_p]:opacity-100!
          [&_td]:max-w-80
          [&_td]:overflow-x-hidden
          [&_td]:text-ellipsis
          min-w-192
        "
      >
        <template #cell(position)="{ item }">
          <a
            :href="item.url"
            target="_blank"
            rel="nofollow noopener noreferrer"
            :class="cellClass"
            class="font-semibold w-full"
            data-id="position"
            tabindex="0"
          >
            {{ item.position }}
          </a>
        </template>
        <template #cell(date_posted)="{ item }">
          <p :class="cellClass">{{ item.date_posted }}</p>
        </template>
        <template #cell(location)="{ item }">
          <div class="flex flex-row flex-nowrap justify-between">
            <p :class="cellClass">{{ item.location }}</p>
            <a
              :href="item.url"
              target="_blank"
              rel="nofollow noopener noreferrer"
              :aria-label="'View ' + item.position + ' posting (opens in new tab)'"
              :class="cellClass"
              class="h-full flex flex-col justify-center pt-px"
              data-id="position"
            >
              <FaIcon
                name="external-link"
                iconClass="text-red-600 w-5 h-5 mt-px opacity-0 group-hover:opacity-100 inline-block"
              />
            </a>
          </div>

        </template>
      </SdsTable>
      <SdsPaginator
        class="mt-4"
        :loading="false"
        :current-page="currentPage"
        :total-pages="totalPages"
        @go-to-page="goToPage"
      />
    </div>
    <CtaLink
      v-if="cta && ctaText"
      :href="cta"
      :external="true"
      :text="ctaText"
    />
  </div>
</template>

<script setup lang="ts">
import { SdsPaginator } from '@cmu-sei/sei-design-system';

const cellClass = [
  'text-black',
  'text-base',
  'text-nowrap',
  'text-ellipsis',
  'opacity-60',
]

interface Job {
  title: string;
  url: URL;
  open_date: Date;
  location: string;
}

interface JobsFeed {
  uuid: string;
  header: string;
  jobs?: Job[];
  cta?: string | null;
  ctaText?: string | null;
  currentPage?: number;
  totalPages?: number;
  pageSize?: number;
  pageStart?: number;
  sortColumn?: string;
  sortDirection?: string;
}
// Define props with type and default values using withDefaults
const props = withDefaults(defineProps<JobsFeed>(), {
  currentPage: 1,
  totalPages: 0,
  pageSize: 0,
  pageStart: 0,
  header: '',
  jobs: () => [],
  cta: null,
  ctaText: null,
  sortColumn: 'open_date',
  sortDirection: 'desc'
});

const rootElement = ref(null);

onBeforeMount(() => {
  rootElement.value = (document.querySelector(`[data-uuid="${props.uuid}"]`) as HTMLElement);
  delete rootElement.value.dataset['json']
});

const sortColumn = ref(props.sortColumn)
const sortDirection = ref(props.sortDirection)

// Map internal column keys (used for data sorting) to display column keys (used by SdsTable)
const displaySortColumn = computed(() => {
  if (sortColumn.value === 'open_date') return 'date_posted'
  if (sortColumn.value === 'title') return 'position'
  return sortColumn.value
})

const fields = ref([
  {
    key: "position",
    label: "Position",
    sortable: true
  },
  {
    key: "date_posted",
    label: "Date posted",
    sortable: true
  },
  {
    key: "location",
    label: "Location",
    sortable: true
  }
])
const currentPage = ref(props.currentPage)
const totalPages = ref(props.totalPages)
const pageSize = ref(props.pageSize)
const pageStart = ref(props.pageStart)
const items = computed(() => {
  interface wrangledItem {
    position: string,
    date_posted: string,
    location: string,
    url: URL
  }
  let itemsList: wrangledItem[] = []
  let jobs = ref(props.jobs || [])
  let rangedArray = []
  // Sort jobs based on sortColumn and sortDirection
  if (sortColumn.value && sortDirection.value) {
    jobs.value.sort((a, b) => {
      let aValue: string, bValue: string;
      if (sortColumn.value === 'location') {
        aValue = a.location["primary"];
        bValue = b.location["primary"];
      } else {
        aValue = a[sortColumn.value];
        bValue = b[sortColumn.value];
      }
      if (aValue < bValue) {
        return sortDirection.value === 'asc' ? -1 : 1;
      }
      if (aValue > bValue) {
        return sortDirection.value === 'asc' ? 1 : -1;
      }
      return 0;
    });
  }

  rangedArray = jobs.value.slice(pageStart.value, pageStart.value + pageSize.value)
  rangedArray.forEach(job => {
    const options: Intl.DateTimeFormatOptions = { year: 'numeric', month: 'long', day: 'numeric' };
    const formattedDate = new Date(job.open_date).toLocaleDateString('en', options);
    const primaryl = job.location["primary"]
    const secondaryl = job.location["secondary"]
    let location = primaryl
    if (primaryl !== secondaryl)
      location = '2 Locations'
    itemsList[itemsList.length] = {
      position: job.title,
      date_posted: formattedDate,
      location: location,
      url: job.url
    }
  })
  return itemsList
})

const goToPage = ({page}) => {
  pageStart.value = (page - 1) * pageSize.value
  currentPage.value = page
};

interface Column {
  title: string;
  date_posted: string;
  position: string;
  location: string;
  sortBy: string;
}

const sortByColumn = (column: Column) => {
  // Custom sorting logic. Map column keys to job object keys
  // for 'date_posted', use 'open_date'
  // for 'position', use 'title'
  // for 'location', use 'location'
  let columnKey = column.sortBy
  if (columnKey === 'date_posted') {
    columnKey = 'open_date'
  } else if (columnKey === 'position') {
    columnKey = 'title'
  } else if (columnKey === 'location') {
    columnKey = 'location'
  } else {
    // If the column is not recognized, do nothing
    return;
  }
  if (sortColumn.value === columnKey) {
    // If the same column is clicked, toggle the sort direction
    if (sortDirection.value === 'desc') {
      sortDirection.value = 'asc'
    } else {
      sortDirection.value = 'desc'
    }
  } else {
    // If a different column is clicked, set the sort column and default direction
    if (columnKey === 'open_date') {
      sortDirection.value = 'desc'
    } else if (columnKey === 'title') {
      sortDirection.value = 'asc'
    } else if (columnKey === 'location') {
      sortDirection.value = 'asc'
    }
  }
  // Update the sort column and reset pagination
  sortColumn.value = columnKey
  currentPage.value = 1
  pageStart.value = 0
}
</script>
